// TermsAcknowledge.js
// Forced-read Terms of Service + Privacy Policy acknowledgment for customers.
// Uses the reusable DocumentAcknowledgment wrapper.
// Saves termsAcknowledged: true and termsAcknowledgedAt to Firestore, then
// redirects to /dashboard (ProtectedRoute will gate on email verification there).
//
// Routes that use this:
//   /terms-acknowledge        — customer version, redirects to /dashboard
//
// NOT wrapped in ProtectedRoute — new customers reach this before email verification.
// DocumentAcknowledgment handles its own auth check internally.

import React from 'react';
import DocumentAcknowledgment from './DocumentAcknowledgment';

// Full Terms of Service content as a React node.
// Exported so TermsAcknowledgeProvider.js can reuse it without duplication.
// Uses .ack-section-heading and .ack-list for consistent styling inside the scroll box.
export const TERMS_CONTENT = (
  <div>

    <p className="ack-content-intro">
      EVERMARK MEMORIAL — TERMS OF SERVICE &amp; PRIVACY POLICY
    </p>

    {/* ===== TERMS OF SERVICE ===== */}

    <h3 className="ack-section-heading">1. Acceptance of Terms</h3>
    <p>
      By accessing or using the Evermark Memorial platform — including our website, mobile site,
      and any related services (collectively, the "Platform") — you agree to be bound by these
      Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Platform.
    </p>
    <p>
      You must be at least 18 years of age to create an account or use the Platform. By using
      Evermark Memorial you represent that you are 18 or older and have the legal capacity to
      enter into a binding agreement.
    </p>
    <p>
      We may update these Terms from time to time. We will notify users of material changes by
      updating the date at the top of this page. Continued use of the Platform after changes are
      posted constitutes your acceptance of the revised Terms.
    </p>

    <h3 className="ack-section-heading">2. Description of Service</h3>
    <p>
      Evermark Memorial is an online marketplace platform that connects customers ("Customers")
      who wish to arrange grave care services with independent local service providers
      ("Caretakers") who fulfill those services. Evermark Memorial is a technology platform and
      marketplace operator — we are not a direct provider of grave care services.
    </p>
    <p>
      Services available through the Platform include one-time visit packages (Memory Visit,
      Care Visit, and Tribute Visit) and recurring monthly subscription plans (Remembrance,
      Guardian, and Legacy). Service descriptions and current pricing are displayed on the
      Platform at the time of booking.
    </p>
    <p>
      Evermark Memorial does not guarantee specific outcomes beyond photo delivery and reasonable
      care standards. We do not guarantee that a Caretaker will be available in every location
      or within any specific timeframe. We reserve the right to modify, suspend, or discontinue
      any part of the Platform at any time with or without notice.
    </p>

    <h3 className="ack-section-heading">3. Independent Contractor Relationship</h3>
    <p>
      Caretakers who provide services through the Evermark Memorial Platform are independent
      contractors, not employees, agents, joint venturers, or partners of Evermark Memorial.
      Evermark Memorial does not supervise, direct, or control the manner, means, timing, or
      methods by which Caretakers perform services.
    </p>
    <p>
      Evermark Memorial is not responsible for and expressly disclaims all liability for the
      conduct, actions, omissions, negligence, or errors of any Caretaker. Customers engage
      Caretakers through the Platform at their own discretion.
    </p>

    <h3 className="ack-section-heading">4. Customer Responsibilities</h3>
    <p>As a Customer, you agree to:</p>
    <ul className="ack-list">
      <li>
        Provide accurate cemetery name, location, and gravesite information when booking.
        Evermark Memorial and Caretakers are not responsible for visits to incorrect locations
        resulting from inaccurate information.
      </li>
      <li>
        Understand and comply with the rules and regulations of the relevant cemetery regarding
        decorations, flowers, and site modifications before placing a booking.
      </li>
      <li>
        Report any disputes or service concerns within 24 hours of receiving the job completion
        notification. Disputes reported after this window will not be eligible for review.
      </li>
      <li>
        Maintain only one account per person. Creating multiple accounts to circumvent
        restrictions or abuse the dispute system is prohibited.
      </li>
      <li>
        Not contact or solicit Caretakers outside of the Platform for services related to those
        found through Evermark Memorial.
      </li>
    </ul>

    <h3 className="ack-section-heading">5. Caretaker Responsibilities</h3>
    <p>As a Caretaker, you agree to:</p>
    <ul className="ack-list">
      <li>Provide services as described with reasonable care, skill, and professionalism.</li>
      <li>Take before and after photos on every visit as a condition of payment release.</li>
      <li>Maintain professional and respectful conduct at all times on cemetery grounds.</li>
      <li>Comply with all cemetery rules, regulations, and posted notices at each site.</li>
      <li>Provide your own supplies, tools, and transportation necessary to perform services.</li>
      <li>
        Be solely responsible for your own tax obligations on all earnings through the Platform.
      </li>
    </ul>

    <h3 className="ack-section-heading">6. Payments and Fees</h3>
    <p>
      All payments are processed securely through Square, Inc. By submitting a booking you
      authorize Evermark Memorial to charge the full amount shown at checkout, including any
      applicable service fees. A service fee of 3–5% may be added to the base service price
      at checkout.
    </p>
    <p>
      Customer funds are held until the Caretaker marks the job complete and both required
      photos have been uploaded. A 24-hour dispute window begins after job completion. If no
      dispute is filed within that window, Caretaker earnings are released for payout.
    </p>
    <p>
      Caretakers receive approximately 65% of the booking value after the Platform fee.
    </p>

    <h3 className="ack-section-heading">7. Cancellations and Refunds</h3>
    <p>
      <strong>Before caretaker claim:</strong> Customers may cancel for a full refund. Refunds
      are processed through Square and may take 5–10 business days.
    </p>
    <p>
      <strong>After caretaker claim:</strong> Cancellation may result in a partial refund at
      Evermark Memorial's discretion. The $22 Tribute Visit flower allowance is non-refundable.
    </p>
    <p>
      <strong>After job completion:</strong> No refunds after the 24-hour dispute window closes.
    </p>

    <h3 className="ack-section-heading">8. Disputes</h3>
    <p>
      Disputes must be filed within 24 hours of receiving the job completion notification via
      the Contact page. Evermark Memorial reviews submitted photos and communications and makes
      a final determination in its sole discretion. Filing a fraudulent dispute may result in
      account suspension.
    </p>

    <h3 className="ack-section-heading">9. Prohibited Conduct</h3>
    <p>You agree not to use the Platform to:</p>
    <ul className="ack-list">
      <li>Create false, duplicate, or fraudulent accounts</li>
      <li>File fraudulent or bad-faith disputes</li>
      <li>Harass, threaten, or abuse any Caretaker, Customer, or team member</li>
      <li>Solicit Caretakers or Customers off-platform to avoid Platform fees</li>
      <li>Violate any cemetery's posted rules or property rights</li>
      <li>Attempt to access other users' accounts or private information</li>
      <li>Upload offensive, defamatory, illegal, or inappropriate content</li>
    </ul>

    <h3 className="ack-section-heading">10. Intellectual Property</h3>
    <p>
      The Evermark Memorial name, logo, platform design, and all associated content are owned by
      Evermark Memorial and protected by applicable law. You may not use Evermark Memorial's
      name, logo, or brand assets without prior written consent.
    </p>

    <h3 className="ack-section-heading">11. Limitation of Liability</h3>
    <p>
      To the maximum extent permitted by applicable law, Evermark Memorial shall not be liable
      for any indirect, incidental, special, consequential, or punitive damages. Evermark
      Memorial's total liability shall not exceed the total amount paid for the specific booking
      giving rise to any claim.
    </p>

    <h3 className="ack-section-heading">12. Indemnification</h3>
    <p>
      You agree to indemnify and hold harmless Evermark Memorial, its founders, officers, and
      agents from any claims, liabilities, damages, losses, and expenses arising out of your
      use of the Platform or violation of these Terms.
    </p>

    <h3 className="ack-section-heading">13. Termination</h3>
    <p>
      Evermark Memorial reserves the right to suspend or permanently terminate any user account
      at any time for violations of these Terms, fraudulent activity, or conduct that harms
      other users or the Platform's reputation.
    </p>

    <h3 className="ack-section-heading">14. Governing Law</h3>
    <p>
      These Terms are governed by the laws of the Commonwealth of Pennsylvania. Any legal
      action shall be brought exclusively in the state or federal courts located in Lawrence
      County, Pennsylvania.
    </p>

    <h3 className="ack-section-heading">15. Contact</h3>
    <p>
      For questions about these Terms, contact us through the Contact page on the Platform.
    </p>

    {/* ===== PRIVACY POLICY (summary) ===== */}
    <h3 className="ack-section-heading" style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
      PRIVACY POLICY — Key Points
    </h3>
    <p>
      Evermark Memorial collects name, email, phone number, city, state, and booking information
      when you create an account and place bookings. This information is used to operate the
      Platform, match bookings with Caretakers, process payments, and communicate with you
      about your service.
    </p>
    <p>
      We do not sell your personal information to third parties. We may share your information
      with Caretakers to the extent necessary to fulfill a booking, and with payment processors
      (Square) to process transactions.
    </p>
    <p>
      Photos submitted by Caretakers may be used for proof of service completion and, with your
      permission, for marketing purposes. You may opt out of marketing use at any time by
      contacting support.
    </p>
    <p>
      You may request deletion of your account and associated data at any time by contacting
      support@evermarkmemorial.com, subject to applicable legal retention requirements.
    </p>
    <p>
      For the full Privacy Policy, visit <a href="/privacy" style={{ color: '#2d6a4f' }}>evermarkmemorial.com/privacy</a>.
    </p>

  </div>
);

function TermsAcknowledge() {
  return (
    <DocumentAcknowledgment
      title="Terms of Service &amp; Privacy Policy"
      content={TERMS_CONTENT}
      checkboxLabel="I have read and agree to the Evermark Memorial Terms of Service and Privacy Policy"
      firestoreField="termsAcknowledged"
      redirectPath="/dashboard"
    />
  );
}

export default TermsAcknowledge;
